<template>
  <div>
    <div>
      <aha-modal
        @hide="hide()"
        :title="modalTitle"
        :action-position="onlyRootOrNone(folders) ? 'justify-end' : 'justify-between'"
      >
        <div v-if="loading" class="text-center">
          <aha-spinner />
        </div>

        <div v-if="!loading && onlyRootOrNone(folders)">
          There’s no folder to move to. Please create a new folder first.
        </div>

        <div class="max-h-48" v-if="!onlyRootOrNone(folders)">
          <aha-radio
            class="folder-list__radio-btn"
            :key="folder.folderId"
            v-for="folder in folders"
            :value="folder.folderId"
            v-model="destinatedFolderId"
            :class="{
              'disabled': folder.disabled,
              'ml-8': folder.folderId
            }"
            :disabled="folder.disabled"
          >
            <span class="folder-list__icon-circle">
              <aha-icon name="folder" class="folder-list__icon" />
            </span>
            <span
              class="folder-list__folder-name"
              :class="{'pr-1': folder.folderName === 'My presentations'}"
            >
              {{ folder.folderName }}
            </span>
          </aha-radio>
        </div>

        <template #action>
          <div name="additional" v-if="!onlyRootOrNone(folders)">
            <div
              class="flex items-center font-bold cursor-pointer text-primary-90"
              @click="handleShowModalCreateNewFolder()"
            >
              <aha-icon
                name="add"
                height="18"
                width="18"
                viewBox="0 0 24 24"
              />
              <p class="pl-2">
                New Folder
              </p>
            </div>
          </div>
          <div class="flex items-center">
            <aha-link @click="hide()">
              <span class="font-bold">Cancel</span>
            </aha-link>
            <aha-button
              :disabled="isMovingPresn"
              @click="handleMovePresn()"
              v-if="!onlyRootOrNone(folders)"
              icon-name="move"
              :submitting="isMovingPresn"
              has-margin-left
            >
              <aha-spinner variant="white" v-if="isMovingPresn" class="mr-1" />
              Move
            </aha-button>
            <aha-button
              @click="handleShowModalCreateNewFolder()"
              v-if="onlyRootOrNone(folders)"
              has-margin-left
            >
              Create Folder
            </aha-button>
          </div>
        </template>
      </aha-modal>
    </div>
    <!-- Need to pass presentationIds array to this modal -->
    <modal-create-new-folder
      :presentationIds="selectedPresentationIds"
      v-if="isModalCreateNewFolderShowing"
      @hide="hide()"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

// modals
import ModalCreateNewFolder from '@/components-v2/modals/CreateNewFolder.vue'

// api
import PresentationApi from '@/api/presentation'
import FolderApi from '@/api/folder'

// eventBus
import EventBus from '@/eventBus'

// utils
import Utils from '@/util'

// gaCategories
import gaCategories from '@/constant/gaCategories'

export default {
  components: {
    ModalCreateNewFolder,
  },
  props: {
    selectedPresentations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isModalCreateNewFolderShowing: false,
      isMovingPresn: false,
      modalTitle: '',
      selectedPresentationIds: [],
      destinatedFolderId: null,
      folders: [],
      loading: false,
    }
  },
  computed: {
    isAllFoldersDisabled() {
      return this.folders.every((f) => f.disabled === true)
    },
  },
  async created() {
    this.generateModalTitle()

    this.loading = true
    const folders = await FolderApi.getFolders()
    folders.unshift({ folderId: null, folderName: 'My presentations' })
    this.folders = folders.map((f) => {
      return {
        ...f,
        disabled: this.selectedPresentations.some((p) => p.folderId === f.folderId),
        folderName: this.selectedPresentations.some((p) => p.folderId === f.folderId) ? `${f.folderName} (current)` : f.folderName,
      }
    })
    const enabled = this.folders.filter((f) => !f.disabled)
    this.destinatedFolderId = enabled && enabled[0] && enabled[0].folderId

    if (this.selectedPresentations.length) {
      this.selectedPresentationIds = this.selectedPresentations.map((presn) => presn.id)
    }
    this.loading = false
  },
  methods: {
    ...mapActions(['setFoldersAction', 'getFoldersAction']),
    onlyRootOrNone(f) {
      return !f.length || !f.some((p) => p.folderId != null)
    },
    gaTrackEvent(label, value) {
      Utils.gaTrackEvent(
        gaCategories.PRESENTATION_LIST,
        label,
        value,
      )
    },
    generateModalTitle() {
      const selectedPresnLength = this.selectedPresentations.length
      if (selectedPresnLength === 1) {
        this.modalTitle = 'Move this presentation to…'
      } else if (selectedPresnLength > 1) {
        this.modalTitle = `Move ${selectedPresnLength} presentations to ...`
      }
    },
    handleBeforeCallAPIMovePresn() {
      this.isMovingPresn = true
      if (this.allFoldersDisabled) {
        Utils.showToastError('Move presentation(s) failed: No folder selected')
        this.isMovingPresn = false
        return
      }
      this.gaTrackEvent('Presentation List > Move Presentation > Submit')
    },
    handleAfterCallAPIMovePresn() {
      this.isMovingPresn = false
      EventBus.$emit('reload-presentations-and-folders')

      this.handleShowToastSuccess()
      this.gaTrackEvent('Presentation List > Move Presentation > Succeeded')
      this.hide()
    },
    handleShowModalCreateNewFolder() {
      this.isModalCreateNewFolderShowing = true
    },
    async handleMovePresn() {
      this.handleBeforeCallAPIMovePresn()
      try {
        const data = {
          presentationIds: this.selectedPresentationIds,
          presentation: { folderId: this.destinatedFolderId },
        }

        await PresentationApi.patchPresentations(data)
        this.handleAfterCallAPIMovePresn()
      } catch (error) {
        this.isMovingPresn = false
        Utils.showToastError('Move presentations failed')
        this.gaTrackEvent(
          'Presentation List > Move Presentation > Failed',
          error.body ? error.body.message : '',
        )
      }
    },
    handleShowToastSuccess() {
      let folderName = 'My presentations'

      if (this.destinatedFolderId) {
        folderName = this.folders
          .find((folder) => folder.folderId === this.destinatedFolderId).folderName
      }

      if (this.selectedPresentationIds && this.selectedPresentationIds.length === 1) {
        Utils.showToastSuccess(`"${this.selectedPresentations[0].name}" moved to "${folderName}"`)
      } else if (this.selectedPresentationIds && this.selectedPresentationIds.length > 1) {
        Utils.showToastSuccess(`${this.selectedPresentationIds.length} presentations moved to "${folderName}"`)
      }
    },
    hide() {
      this.$emit('hide')
    },
  },
}
</script>

<style lang="scss" scoped>
.folder-list__radio-btn {
  @apply pr-2 mb-2;
  .folder-list__icon-circle {
    @apply rounded-full bg-purple-10 w-max p-2;
    .folder-list__icon {
      @apply text-purple-50;
    }
  }
  .folder-list__folder-name {
    @apply pl-2 overflow-hidden overflow-ellipsis;
  }
  &.disabled {
    &.radio-component {
      @apply cursor-not-allowed;
    }
    .folder-list__icon-circle {
    @apply bg-base-10;
    .folder-list__icon {
      @apply text-base-60;
    }
  }
    .folder-list__folder-name {
      @apply text-base-80
    }
  }
}
</style>
